import {Card, FormControl, FormField, Parameters, RadioItem, Separator} from 'platform/components';
import {Box, Heading, HStack, Show, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import {CurrencyCodeType, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {PRICING_FIELD_WIDTH} from '../constants/PRICING_FIELD_WIDTH';
import {DiscountSectionFormType} from '../types/discountSectionSchema';

interface SmallUnitMaterialCardProps extends RequiredTestIdProps {
  control: FormControl<DiscountSectionFormType>;
  formApi: UseFormReturn<DiscountSectionFormType>;
  isReadOnly?: boolean;
  currency: CurrencyCodeType;
  whatIfAmountWithoutVat?: number | null;
  whatIfAmountWithVat?: number | null;
}

export function SmallUnitMaterialCard(props: SmallUnitMaterialCardProps) {
  const formatCurrency = useFormatCurrency();

  const isExpanded = useWatch({control: props.control, name: 'isSmallUnitMaterialExpanded'});
  const isFixedValueWithVat = useWatch({control: props.control, name: 'isFixedValueWithVat'});

  const hasWhatIfAmounts =
    !isNil(props.whatIfAmountWithoutVat) || !isNil(props.whatIfAmountWithVat);

  return (
    <Card variant="inlineGrey">
      <VStack spacing={4}>
        <VStack spacing={1}>
          <Heading size={4}>{i18n.t('entity.order.labels.smallUnitMaterial')}</Heading>
          <Show when={hasWhatIfAmounts}>
            <Parameters
              parameters={[
                i18n.t('entity.order.labels.currentCalculatedPrice'),
                `${formatCurrency(props.whatIfAmountWithoutVat ?? 0, props.currency)} ${i18n.t('general.labels.withoutVat')}`,
                `${formatCurrency(props.whatIfAmountWithVat ?? 0, props.currency)} ${i18n.t('general.labels.withVat')}`,
              ]}
              size="small"
              data-testid={suffixTestId('currentCalculatedPrice', props)}
            />
          </Show>
        </VStack>
        <Separator spacing={0} />
        <HStack align="center" spacing={3}>
          <FormField
            control={props.control}
            name="isSmallUnitMaterialExpanded"
            type="switch"
            isDisabled={props.isReadOnly}
            data-testid={suffixTestId('expand-switch', props)}
          />
          <Heading size={4}>{i18n.t('entity.order.labels.customSmallUnitMaterial')}</Heading>
        </HStack>
        <Show when={isExpanded}>
          <VStack spacing={4}>
            <HStack spacing={4}>
              <Box width={PRICING_FIELD_WIDTH} minWidth={0}>
                <RadioItem
                  value={isFixedValueWithVat === false}
                  onChange={() => {
                    props.formApi.setValue('isFixedValueWithVat', false, {shouldDirty: true});
                    props.formApi.setValue('smallUnitMaterial.fixedValueAmountWithoutVat', null, {
                      shouldDirty: true,
                    });
                    props.formApi.setValue('smallUnitMaterial.fixedValueAmountWithVat', null, {
                      shouldDirty: true,
                    });
                  }}
                  isDisabled={props.isReadOnly}
                  data-testid={suffixTestId('vat-options', props)}
                >
                  {i18n.t('general.labels.priceWithoutVat')}
                </RadioItem>
              </Box>
              <Box width={PRICING_FIELD_WIDTH} minWidth={0}>
                <RadioItem
                  value={isFixedValueWithVat === true}
                  onChange={() => {
                    props.formApi.setValue('isFixedValueWithVat', true, {shouldDirty: true});
                    props.formApi.setValue('smallUnitMaterial.fixedValueAmountWithoutVat', null, {
                      shouldDirty: true,
                    });
                    props.formApi.setValue('smallUnitMaterial.fixedValueAmountWithVat', null, {
                      shouldDirty: true,
                    });
                  }}
                  isDisabled={props.isReadOnly}
                  data-testid={suffixTestId('vat-options', props)}
                >
                  {i18n.t('general.labels.priceWithVat')}
                </RadioItem>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <Box width={PRICING_FIELD_WIDTH} minWidth={0}>
                <FormField
                  control={props.control}
                  name="smallUnitMaterial.fixedValueAmountWithoutVat"
                  type="currency"
                  currency={props.currency}
                  isDisabled={props.isReadOnly || isFixedValueWithVat}
                  data-testid={suffixTestId('fixed-amount', props)}
                />
              </Box>
              <Box width={PRICING_FIELD_WIDTH} minWidth={0}>
                <FormField
                  control={props.control}
                  name="smallUnitMaterial.fixedValueAmountWithVat"
                  type="currency"
                  currency={props.currency}
                  isDisabled={props.isReadOnly || !isFixedValueWithVat}
                  data-testid={suffixTestId('fixed-amount', props)}
                />
              </Box>
            </HStack>
          </VStack>
        </Show>
      </VStack>
    </Card>
  );
}
